import { DomainError, ErrorCode } from "@gladis/shared";

/**
 * The refusals this context can return. Twenty-two, each naming WHY.
 *
 * A refusal that only says "you can't" is an obstacle; one that says which rule was broken lets the
 * interface offer the next step. That is the reasoning `IDENT_OWNER_CANNOT_DELETE` established, the
 * `SAV_*` block inherited and the `PROP_*` block continued.
 *
 * THE DERIVED-LINES FAILURE IS NOT HERE. If the properties context cannot produce the monthly interest,
 * FR-076 requires the page to render and SAY the section is unavailable rather than fail — so the
 * composition root handles that `Result` into a payload flag. An error class here would imply an HTTP
 * status the read route must never return, and would invite someone to throw it.
 */

// ---------------------------------------------------------------------------
// Expenses
// ---------------------------------------------------------------------------

export class ExpenseNotFoundError extends DomainError {
  constructor(expenseId?: string) {
    // Identical to the response for an expense in another household, deliberately: the identifier must
    // disclose nothing about whether it exists somewhere the caller cannot see.
    super(ErrorCode.EXP_NOT_FOUND, "That expense could not be found.", 404, { expenseId });
  }
}

export class ExpenseArchivedError extends DomainError {
  constructor() {
    // Distinct from ExpenseNotFoundError on purpose. The expense exists and the caller may see it; the
    // refusal is about its state, and unarchiving is the next step. Collapsing the two would tell
    // someone their expense had vanished.
    super(
      ErrorCode.EXP_ARCHIVED,
      "That expense is archived. Unarchive it first to make changes.",
      409,
    );
  }
}

export class ExpenseNotArchivedError extends DomainError {
  constructor() {
    super(ErrorCode.EXP_NOT_ARCHIVED, "That expense is not archived.", 409);
  }
}

export class ExpenseAlreadyArchivedError extends DomainError {
  constructor() {
    super(ErrorCode.EXP_ALREADY_ARCHIVED, "That expense is already archived.", 409);
  }
}

export class ExpenseAmountOutOfRangeError extends DomainError {
  constructor(limit: number) {
    /*
     * Zero and negative land here too, not only the column's ceiling.
     *
     * The bound is the `numeric(15,2)` column's own, not an invented product ceiling, and it exists so
     * that no value capable of failing AT the database is ever sent to it: an expected failure is
     * returned, never thrown (Principle V).
     */
    super(
      ErrorCode.EXP_AMOUNT_OUT_OF_RANGE,
      `Enter an amount greater than €0.00 and at most €${limit.toLocaleString("en-GB")}.`,
      422,
      { limit },
    );
  }
}

export class ExpenseDescriptionInvalidError extends DomainError {
  constructor() {
    super(ErrorCode.EXP_DESCRIPTION_INVALID, "Enter a description.", 422);
  }
}

export class ExpenseCategoryUnknownError extends DomainError {
  constructor(value: string) {
    super(ErrorCode.EXP_CATEGORY_UNKNOWN, "That is not a category we recognise.", 422, { value });
  }
}

export class ExpenseIntervalUnknownError extends DomainError {
  constructor(value: string) {
    super(ErrorCode.EXP_INTERVAL_UNKNOWN, "That is not a recurrence interval we recognise.", 422, {
      value,
    });
  }
}

export class ExpenseStartDateInvalidError extends DomainError {
  constructor(message = "Enter a start date as YYYY-MM-DD.", metadata?: Record<string, unknown>) {
    super(ErrorCode.EXP_START_DATE_INVALID, message, 422, metadata);
  }
}

export class ExpensesFirstPaymentDateRequiredError extends DomainError {
  constructor() {
    super(
      ErrorCode.EXP_FIRST_PAYMENT_DATE_REQUIRED,
      "The interval changed, so enter the new first payment date — or clear it to leave the expense undated.",
      422,
    );
  }
}

// ---------------------------------------------------------------------------
// Splits
// ---------------------------------------------------------------------------

export class SplitNotHundredError extends DomainError {
  constructor(actualPercentage: number) {
    /*
     * CARRIES THE ACTUAL SUM, which is the whole reason this is its own class.
     *
     * "The percentages must total 100%" is not actionable; "they total 90%" is. The form states the
     * current sum from this metadata rather than re-deriving it from the shares it just sent — which
     * matters when some of those shares are frozen and the form did not choose them.
     */
    super(
      ErrorCode.EXP_SPLIT_NOT_HUNDRED,
      `The shares add up to ${actualPercentage}%. They must total exactly 100%.`,
      422,
      { actualPercentage },
    );
  }
}

export class SplitShareInvalidError extends DomainError {
  constructor(value: number) {
    super(
      ErrorCode.EXP_SPLIT_SHARE_INVALID,
      "Each share must be more than 0%, at most 100%, and use at most two decimal places.",
      422,
      { value },
    );
  }
}

export class SplitDuplicateMemberError extends DomainError {
  constructor(memberId: string) {
    super(ErrorCode.EXP_SPLIT_DUPLICATE_MEMBER, "A member can hold only one share.", 422, {
      memberId,
    });
  }
}

export class SplitMemberNotAllowedError extends DomainError {
  constructor(memberId: string) {
    /*
     * Neither a current active member NOR already stored on this expense.
     *
     * The second half is the interesting one. A share belonging to someone who has since left the
     * household may be CARRIED FORWARD by a later edit but never created — which is what lets an
     * ordinary amount correction leave their percentage exactly where it was, instead of dropping it
     * and leaving the split short of 100%. See research.md R10.
     */
    super(
      ErrorCode.EXP_SPLIT_MEMBER_NOT_ALLOWED,
      "Only current members of this household can be given a share.",
      422,
      { memberId },
    );
  }
}

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

export class CardNotFoundError extends DomainError {
  constructor(cardId?: string) {
    super(ErrorCode.CARD_NOT_FOUND, "That card could not be found.", 404, { cardId });
  }
}

export class CardArchivedError extends DomainError {
  constructor() {
    // Crosses between entities: the expense is fine, the card it names is not.
    super(
      ErrorCode.CARD_ARCHIVED,
      "That card is archived. Unarchive it, or choose another card.",
      409,
    );
  }
}

export class CardNotArchivedError extends DomainError {
  constructor() {
    super(ErrorCode.CARD_NOT_ARCHIVED, "That card is not archived.", 409);
  }
}

export class CardAlreadyArchivedError extends DomainError {
  constructor() {
    super(ErrorCode.CARD_ALREADY_ARCHIVED, "That card is already archived.", 409);
  }
}

export class CardNameInvalidError extends DomainError {
  constructor() {
    super(ErrorCode.CARD_NAME_INVALID, "Give the card a name.", 422);
  }
}

export class CardOwnerRequiredError extends DomainError {
  constructor() {
    // An individual card must HAVE an owner when it is created. It need not have one forever, which is
    // why this rule lives here and not in a check constraint — see migration 20260810000000.
    super(ErrorCode.CARD_OWNER_REQUIRED, "Choose who owns this card, or make it joint.", 422);
  }
}

export class CardOwnerNotAllowedError extends DomainError {
  constructor() {
    super(
      ErrorCode.CARD_OWNER_NOT_ALLOWED,
      "A joint card has no single owner. Make it individual to choose one.",
      422,
    );
  }
}

export class CardOwnerNotMemberError extends DomainError {
  constructor(ownerId: string) {
    super(
      ErrorCode.CARD_OWNER_NOT_MEMBER,
      "The owner must be a current member of this household.",
      422,
      { ownerId },
    );
  }
}

export class CardIdentifierInvalidError extends DomainError {
  constructor(message: string, metadata?: Record<string, unknown>) {
    /*
     * The message varies because the two halves fail differently, and neither failure is about a full
     * account number — the application holds a short bank code and four digits, because nothing here
     * initiates a payment and the local database is dumped to plaintext as part of the normal workflow.
     */
    super(ErrorCode.CARD_IDENTIFIER_INVALID, message, 422, metadata);
  }
}
